import { useEffect, useState, useCallback, useRef } from 'react';
import webSocketService, { WebSocketNotification, WebSocketEventHandlers } from '../../services/websocket/websocketService';

interface UseWebSocketReturn {
  isConnected: boolean;
  reconnectAttempts: number;
  connect: (token: string) => void;
  disconnect: () => void;
  markNotificationAsRead: (notificationId: number) => void;
  requestUnreadCount: () => void;
}

interface UseWebSocketOptions {
  onNewNotification?: (notification: WebSocketNotification) => void;
  onUnreadCountUpdate?: (data: { count: number }) => void;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  autoConnect?: boolean;
  token?: string;
}

/**
 * Hook para gerenciar conexão WebSocket
 */
export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const optionsRef = useRef(options);
  
  // Atualizar ref quando options mudam
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Event handlers memoizados
  const handleConnect = useCallback(() => {
    setIsConnected(true);
    setReconnectAttempts(0);
    optionsRef.current.onConnect?.();
  }, []);

  const handleDisconnect = useCallback((reason: string) => {
    setIsConnected(false);
    optionsRef.current.onDisconnect?.(reason);
  }, []);

  const handleError = useCallback((error: Error) => {
    const stats = webSocketService.getConnectionStats();
    setReconnectAttempts(stats.reconnectAttempts);
    optionsRef.current.onError?.(error);
  }, []);

  const handleNewNotification = useCallback((notification: WebSocketNotification) => {
    optionsRef.current.onNewNotification?.(notification);
  }, []);

  const handleUnreadCountUpdate = useCallback((data: { count: number }) => {
    optionsRef.current.onUnreadCountUpdate?.(data);
  }, []);

  // Funções de controle
  const connect = useCallback((token: string) => {
    const handlers: WebSocketEventHandlers = {
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
      onError: handleError,
      onNewNotification: handleNewNotification,
      onUnreadCountUpdate: handleUnreadCountUpdate,
    };

    webSocketService.connect(token, handlers);
  }, [handleConnect, handleDisconnect, handleError, handleNewNotification, handleUnreadCountUpdate]);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
    setIsConnected(false);
    setReconnectAttempts(0);
  }, []);

  const markNotificationAsRead = useCallback((notificationId: number) => {
    webSocketService.markNotificationAsRead(notificationId);
  }, []);

  const requestUnreadCount = useCallback(() => {
    webSocketService.requestUnreadCount();
  }, []);

  // Auto connect se token fornecido
  useEffect(() => {
    if (options.autoConnect && options.token && !isConnected) {
      connect(options.token);
    }

    // Cleanup na desmontagem
    return () => {
      if (isConnected) {
        disconnect();
      }
    };
  }, [options.autoConnect, options.token, isConnected, connect, disconnect]);

  // Verificar estado inicial
  useEffect(() => {
    const initialState = webSocketService.isConnected();
    setIsConnected(initialState);
  }, []);

  return {
    isConnected,
    reconnectAttempts,
    connect,
    disconnect,
    markNotificationAsRead,
    requestUnreadCount,
  };
};

export default useWebSocket;
